import {ChangeEvent} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {History} from 'history';
import humanizeRange from '@pluto/humanize-moment-range';
import {DateRangeState} from '@pluto/daterange-picker/DateRangeTypes';
import {SetNumberValueType, DebounceParams} from './types';
import {Sort, SortingOptionsValues} from '../table';

export const handlePaging = curry((containerArgs, tablePaging) => {
    const {setList, setQuery, history, route, ...containerParams} = containerArgs;
    const params = {
        ...containerParams,
        ...tablePaging,
    };

    setQuery(history, route, params);
    setList(params);
});

export const handleDebounce = curry(
    ({debouncedFunction, setSearchFilter}, {target: {value}}) => {
        setSearchFilter(value);
        debouncedFunction(value);
    }
);

export const handleDebounceCallback = <
    T extends {
        setList: (params: DebounceParams<T>) => void;
        setPage: SetNumberValueType;
        setQuery: <T extends unknown>(
            history: History,
            pathname: string,
            params: DebounceParams<T>
        ) => void;
        history: History;
        route: string;
    }
>(
    handlers: T
) => {
    const {setList, setPage, setQuery, history, route, ...params} = handlers;
    return () => {
        setPage(0);
        setQuery(history, route, params);
        setList(params);
    };
};

export const handleMount = wrap(
    <
        G,
        T extends {
            setList: (params: G) => void;
            resetList: SetNumberValueType;
            setPage: SetNumberValueType;
            setRowsPerPage: SetNumberValueType;
            params: G;
            query: URLSearchParams;
        }
    >(
        props: T
    ) => {
        const {setList, resetList, setPage, setRowsPerPage, params, query} = props;
        const queryLimit = query.get('limit');

        if (queryLimit) {
            const queryOffset = query.get('offset');
            const limit = Number(queryLimit);
            const page = Number(queryOffset) / limit || 0;
            setRowsPerPage(limit);
            setPage(page);
            setList({
                ...params,
                limit: Number(queryLimit),
            });
            return;
        }

        setList(params);

        return resetList;
    }
);

export const handleFilters = curry(
    (presets, {target: {value}}: ChangeEvent<HTMLInputElement>) => {
        const {
            setFilter,
            setPage,
            setList,
            setQuery,
            key,
            history,
            route,
            ...restPresets
        } = presets;

        const newFilters = {
            ...restPresets,
            [key]: value,
        };
        const query = {
            ...newFilters,
            offset: 0,
        };

        setPage(0);
        setQuery(history, route, query);
        setFilter(newFilters);
        setList(query);
    }
);

export const getDateRange = (sort: string, [dateStart, dateEnd]: [string, string]) => ({
    [`${sort}Start`]: dateStart,
    [`${sort}End`]: dateEnd,
});

export const handleDateRangeChange = curry(
    (containerParams, sort: string, setDates, dates: DateRangeState) => {
        const {
            setPage,
            setQuery,
            history,
            setList,
            limit,
            sortOrder,
            route,
            ...restParams
        } = containerParams;

        const humanizeDate = humanizeRange(dates);
        const dateRange = getDateRange(sort, humanizeDate);

        const query = {
            offset: 0,
            limit,
            sortOrder,
            ...restParams,
            ...dateRange, // dateRange have to be under restParams to rewrite multiple filters values
        };

        setPage(0);
        setQuery(history, route, query);
        setDates(dates);
        setList(query);
    }
);

export const getNewSort = (sort: Sort) => {
    switch (sort.sortOrder) {
        case SortingOptionsValues.desc:
            return {};

        case SortingOptionsValues.asc:
            return {
                sortBy: sort.sortBy,
                sortOrder: SortingOptionsValues.desc,
            };

        default:
            return {
                sortBy: sort.sortBy,
                sortOrder: SortingOptionsValues.asc,
            };
    }
};

export const handleSort = curry((componentParams, oldSort) => {
    const {setSort, setList, setQuery, history, params, route} = componentParams;

    const newSort = getNewSort(oldSort);
    setSort(newSort);

    const newQuery = {
        ...params,
        sortBy: newSort.sortBy,
        sortOrder: newSort.sortOrder,
    };

    setQuery(history, route, newQuery);
    setList(newQuery);
});
